import type { BlockSpec } from '@blocksuite/block-std';
import {
  AFFINE_EDGELESS_COPILOT_WIDGET,
  AffineDragHandleWidget,
  AffineImageToolbarWidget,
  AffineSlashMenuWidget,
  EdgelessCopilotWidget,
  EdgelessElementToolbarWidget,
  EdgelessRootBlockSpec,
  ImageBlockSpec,
  PageRootBlockSpec,
  ParagraphBlockService,
  ParagraphBlockSpec,
} from '@blocksuite/blocks';
import { assertInstanceOf } from '@blocksuite/global/utils';
import { literal, unsafeStatic } from 'lit/static-html.js';

import { buildAIPanelConfig } from './_common/ai-handler';
import {
  setupEdgelessCopilot,
  setupEdgelessElementToolbarEntry,
} from './entries/edgeless/index';
import { setupDragHandleEntry } from './entries/pique/setup-hover-dock';
import { setupSlashMenuEntry } from './entries/slash-menu/setup-slash-menu';
import {
  PIQUE_AI_PANEL_WIDGET,
  PIQUE_VIEWPORT_OVERLAY_WIDGET,
  PiqueAIPanelWidget,
} from './widgets';

const AFFINE_EDGELESS_AUTO_CONNECT_WIDGET =
  'affine-edgeless-auto-connect-widget';

// Remove original AI panel widgets from inherited widgets
const AFFINE_AI_PANEL_WIDGET = 'affine-ai-panel-widget';
const AFFINE_VIEWPORT_OVERLAY_WIDGET = 'affine-viewport-overlay-widget';

export const AIPageRootBlockSpec: BlockSpec = {
  ...PageRootBlockSpec,
  view: {
    ...PageRootBlockSpec.view,
    widgets: {
      // Remove original AI panel widgets and add our custom ones
      ...Object.fromEntries(
        Object.entries(PageRootBlockSpec.view.widgets ?? {}).filter(
          ([key]) =>
            key !== AFFINE_AI_PANEL_WIDGET &&
            key !== AFFINE_VIEWPORT_OVERLAY_WIDGET
        )
      ),
      [PIQUE_AI_PANEL_WIDGET]: literal`${unsafeStatic(PIQUE_AI_PANEL_WIDGET)}`,
      [PIQUE_VIEWPORT_OVERLAY_WIDGET]: literal`${unsafeStatic(
        PIQUE_VIEWPORT_OVERLAY_WIDGET
      )}`,
    },
  },
  setup: (slots, disposableGroup) => {
    PageRootBlockSpec.setup?.(slots, disposableGroup);

    disposableGroup.add(
      slots.widgetConnected.on(view => {
        if (view.component instanceof PiqueAIPanelWidget) {
          view.component.config = buildAIPanelConfig(view.component);
        }
        // Pique Update - comment out patching the format bar with ai button
        // if (view.component instanceof AffineFormatBarWidget) {
        //   setupFormatBarEntry(view.component);
        // }

        if (view.component instanceof AffineSlashMenuWidget) {
          setupSlashMenuEntry(view.component);
        }

        if (view.component instanceof AffineDragHandleWidget) {
          setupDragHandleEntry(view.component);
        }
      })
    );
  },
};

export const AIEdgelessRootBlockSpec: BlockSpec = {
  ...EdgelessRootBlockSpec,
  view: {
    ...EdgelessRootBlockSpec.view,
    widgets: {
      // Remove original AI panel widgets and auto-connect widget, add our custom ones
      ...Object.fromEntries(
        Object.entries(EdgelessRootBlockSpec.view.widgets ?? {}).filter(
          ([key]) =>
            key !== AFFINE_EDGELESS_AUTO_CONNECT_WIDGET &&
            key !== AFFINE_AI_PANEL_WIDGET &&
            key !== AFFINE_VIEWPORT_OVERLAY_WIDGET
        )
      ),
      [AFFINE_EDGELESS_COPILOT_WIDGET]: literal`${unsafeStatic(
        AFFINE_EDGELESS_COPILOT_WIDGET
      )}`,
      [PIQUE_AI_PANEL_WIDGET]: literal`${unsafeStatic(PIQUE_AI_PANEL_WIDGET)}`,
      [PIQUE_VIEWPORT_OVERLAY_WIDGET]: literal`${unsafeStatic(
        PIQUE_VIEWPORT_OVERLAY_WIDGET
      )}`,
    },
  },
  setup(slots, disposableGroup) {
    EdgelessRootBlockSpec.setup?.(slots, disposableGroup);

    slots.widgetConnected.on(view => {
      if (view.component instanceof PiqueAIPanelWidget) {
        view.component.style.width = '430px';
        view.component.config = buildAIPanelConfig(view.component);
      }

      if (view.component instanceof EdgelessCopilotWidget) {
        setupEdgelessCopilot(view.component);
      }

      // EdgelessElementToolbarWidget is responsible for setting up toolbar buttons based on edgeless element selection
      if (view.component instanceof EdgelessElementToolbarWidget) {
        setupEdgelessElementToolbarEntry(view.component);
      }

      if (view.component instanceof AffineSlashMenuWidget) {
        setupSlashMenuEntry(view.component);
      }
    });
  },
};

export const AIParagraphBlockSpec: BlockSpec = {
  ...ParagraphBlockSpec,
  setup(slots, disposableGroup) {
    ParagraphBlockSpec.setup?.(slots, disposableGroup);

    slots.mounted.on(({ service }) => {
      assertInstanceOf(service, ParagraphBlockService);
      service.placeholderGenerator = model => {
        if (model.type === 'text') {
          return 'Take notes your way...';
        }

        const placeholders = {
          h1: 'Heading 1',
          h2: 'Heading 2',
          h3: 'Heading 3',
          h4: 'Heading 4',
          h5: 'Heading 5',
          h6: 'Heading 6',
          quote: '',
        };
        return placeholders[model.type];
      };
    });
  },
};

export const AIImageBlockSpec: BlockSpec = {
  ...ImageBlockSpec,
  setup(slots, disposableGroup) {
    ImageBlockSpec.setup?.(slots, disposableGroup);

    slots.widgetConnected.on(view => {
      if (view.component instanceof AffineImageToolbarWidget) {
        // setupImageToolbarEntry(view.component);
      }
    });
  },
};
